import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Award, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AppearanceMenu } from "@/components/AppearanceMenu";
import { Logo } from "@/components/Logo";
import { Loading } from "@/components/States";
import { Avatar } from "@/components/ShowcaseExtras";
import { InviteDialog } from "@/components/hiring/InviteDialog";
import { useAuth } from "@/context/AuthContext";
import { api, fetchSubjects } from "@/lib/api";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;
const ALL = "all";

function TalentCard({ t, onInvite }) {
  return (
    <div className="card-hover flex flex-col rounded-2xl border border-line bg-surface">
    <Link to={`/learner/${t.handle}`} data-testid={`talent-card-${t.handle}`} className="group flex items-start gap-4 p-5">
      <Avatar name={t.name} photoUrl={t.photo_url ? `${ORIGIN}${t.photo_url}` : null} size="h-14 w-14 text-lg" />
      <div className="min-w-0 flex-1">
        <p className="font-bold text-ink">{t.name}</p>
        {t.headline && <p className="text-sm text-ink-2">{t.headline}</p>}
        <p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-aqua"><Award className="h-3.5 w-3.5" />{t.total_certificates} verified certificate{t.total_certificates === 1 ? "" : "s"}</p>
        {t.certificates.length > 0 && <p className="mt-1 line-clamp-2 text-xs text-ink-3">{t.certificates.join(" · ")}</p>}
      </div>
      <ArrowUpRight className="h-4 w-4 shrink-0 text-ink-3 transition-colors group-hover:text-aqua" />
    </Link>
    {onInvite && <Button size="sm" data-testid={`talent-invite-${t.handle}`} onClick={() => onInvite(t.handle)} className="mx-5 mb-4 self-end rounded-full bg-aqua-solid hover:bg-aqua-solid-hover">Invite to apply</Button>}
    </div>
  );
}

function Filters({ f, setF, subjects, courses }) {
  return (
    <div data-testid="talent-filters" className="grid gap-3 rounded-2xl border border-line bg-surface p-4 md:grid-cols-[1fr_220px_280px]">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
        <Input data-testid="talent-search-input" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search name or headline" className="pl-9" />
      </div>
      <Select value={f.subject} onValueChange={(v) => setF({ ...f, subject: v })}>
        <SelectTrigger data-testid="talent-subject-select"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value={ALL}>Any subject mastery</SelectItem>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={f.course} onValueChange={(v) => setF({ ...f, course: v })}>
        <SelectTrigger data-testid="talent-course-select"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value={ALL}>Any IDDCR course</SelectItem>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export default function Talent() {
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [f, setF] = useState({ q: "", subject: ALL, course: ALL });
  const [res, setRes] = useState(null);
  const { user } = useAuth();
  const [inviting, setInviting] = useState(null);
  const canInvite = user && user.role === "employer" && user.employer_status === "approved";
  useEffect(() => { fetchSubjects().then(setSubjects); api.get("/courses").then((r) => setCourses(r.data)); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      const params = { q: f.q, ...(f.subject !== ALL && { subject: f.subject }), ...(f.course !== ALL && { course: f.course }) };
      api.get("/public/talent", { params }).then((r) => setRes(r.data)).catch(() => setRes({ count: 0, items: [] }));
    }, 250);
    return () => clearTimeout(t);
  }, [f]);
  const filtered = f.q || f.subject !== ALL || f.course !== ALL;
  return (
    <div className="grid-bg min-h-screen bg-canvas px-4 py-10">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-3"><Link to="/" className="shrink-0"><Logo className="h-10" /></Link>
          <div className="flex flex-wrap items-center gap-2"><AppearanceMenu />
            {user?.role === "employer" ? <Button asChild size="sm" className="rounded-full bg-solid"><Link to="/employer" data-testid="talent-dashboard-link">Employer dashboard</Link></Button> : user ? null : (
              <><Button asChild size="sm" className="rounded-full bg-solid"><Link to="/employer/register" data-testid="talent-employer-link">Post jobs as an employer</Link></Button><Button asChild variant="outline" size="sm" className="rounded-full"><Link to="/register" data-testid="talent-join-link">Learners: join LearnAI</Link></Button></>
            )}
          </div>
        </div>
        <div className="mb-8 mt-12 fade-up">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-aqua">IDDCR LearnAI · Find talent</p>
          <h1 className="mt-3 text-4xl font-extrabold text-ink sm:text-5xl">Verified clinical research talent</h1>
          <p className="mt-3 max-w-2xl text-ink-2">Browse learners who have chosen to share their IDDCR certificates. Every certificate is verified live by IDDCR Global Institute.</p>
        </div>
        <Filters f={f} setF={setF} subjects={subjects} courses={courses} />
        {!res ? <Loading /> : (
          <>
            <p data-testid="talent-count" className="mb-4 mt-6 text-sm text-ink-3"><b className="text-ink">{res.count}</b> learner{res.count === 1 ? "" : "s"}{filtered ? " match your filters" : " with public showcases"}</p>
            {res.items.length === 0 ? (
              <div data-testid="talent-empty" className="rounded-2xl border border-dashed border-line-strong bg-canvas-2/60 p-8 text-sm text-ink-2">No learners match these filters yet. Try a different subject or course.</div>
            ) : (
              <div data-testid="talent-list" className="grid grid-cols-1 gap-4 md:grid-cols-2">{res.items.map((t) => <TalentCard key={t.handle} t={t} onInvite={canInvite ? setInviting : null} />)}</div>
            )}
          </>
        )}
      </div>
      <InviteDialog handle={inviting} onClose={() => setInviting(null)} />
    </div>
  );
}
